<script setup lang="ts">
import LocationIcon from '@/components/icons/locationIcon.vue'
import { Winemaker } from '@/types'

interface Props {
    winemaker: Winemaker
}

const props = defineProps<Props>()

const handleClick = () => {
    if (props.winemaker.website) {
        // Open website in new tab
        window.open(props.winemaker.website, '_blank', 'noopener,noreferrer')
    }
}

// Height checker directive for text wrap
const vCheckTallText = {
    mounted(el: HTMLElement) {
        // Check height after content is rendered
        requestAnimationFrame(() => {
            const height = el.getBoundingClientRect().height
            if (height > 93) {
                el.classList.add('winemaker-card__text-wrap_tall')
            }
        })
    },
    updated(el: HTMLElement) {
        // Check height after content updates
        requestAnimationFrame(() => {
            const height = el.getBoundingClientRect().height
            el.classList.toggle('winemaker-card__text-wrap_tall', height > 93)
        })
    },
}
</script>

<template>
    <div
        class="winemaker-card"
        :style="{ backgroundImage: `url(${winemaker.bg_image?.url})` }"
        @click="handleClick"
    >
        <div class="winemaker-card__wrap">
            <div v-if="winemaker.logo_image?.url" class="winemaker-card__logo">
                <img :src="winemaker.logo_image.url" alt="" />
            </div>
            <div class="winemaker-card__text-wrap" v-check-tall-text>
                <div class="winemaker-card__region">
                    <div class="winemaker-card__region-icon">
                        <LocationIcon />
                    </div>
                    <div class="winemaker-card__region-label">
                        {{ winemaker.region || '—' }}
                    </div>
                </div>
                <div class="winemaker-card__title">{{ winemaker.name }}</div>
                <div class="winemaker-card__desc">{{ winemaker.short_description || ' ' }}</div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="stylus">
.winemaker-card
    position relative
    width 100%
    height 168px
    border-radius 12px
    overflow hidden
    background-size cover
    background-position center
    background-repeat no-repeat
    &__wrap
        position absolute
        top 0
        left 0
        display flex
        flex-direction column
        justify-content space-between
        align-items flex-start
        width 100%
        height 100%
        box-sizing border-box
        padding 12px
        background linear-gradient(15deg,rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.35) 100%)
    &__logo
        width auto
        max-width 100px
        height 48px
        align-self flex-end
        display flex
        align-items center
        justify-content center
        border-radius 8px
        background-color var(--app-bg-color)
        padding 3px 10px
        & img
            display block
            object-fit contain
    &__region
        display flex
        align-items center
        gap 4px
        margin-bottom 6px
        font-size 12px
        line-height 12px
        color var(--app-text-light-color)
        opacity 0.7
        &-icon
            width 12px
            height 12px
        &-text
            font-size 10px
            line-height 12px
    &__title
        font-size 18px
        line-height 21px
        font-weight 600
        color var(--app-text-light-color)
    &__desc
        display block
        display -webkit-box
        -webkit-line-clamp 3
        -webkit-box-orient vertical
        overflow hidden
        text-overflow ellipsis
        max-width 100%
        max-height 57px
        margin-top 12px
        font-size 14px
        line-height 19px
        color var(--app-text-light-color)
    &__text-wrap_tall
        margin-top -20px
</style>
